import React, { createContext, useContext, useState, useEffect } from "react";
import { getApiUrl } from '../config/api';

const WishlistContext = createContext();

export const WishlistProvider = ({ children }) => {
  const [wishlist, setWishlist] = useState(() => {
    if (localStorage.getItem('token')) return [];
    const data = localStorage.getItem('guest_wishlist');
    return data ? JSON.parse(data) : [];
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = localStorage.getItem('token');

  // Helper to load wishlist from localStorage for guests
  const loadGuestWishlist = () => {
    try {
      const data = localStorage.getItem('guest_wishlist');
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  };
  // Helper to save wishlist to localStorage for guests
  const saveGuestWishlist = (items) => {
    localStorage.setItem('guest_wishlist', JSON.stringify(items));
  };

  // Move fetchWishlist outside useEffect so it can be used elsewhere
  const fetchWishlist = async () => {
    if (!token) {
      const guestWishlist = loadGuestWishlist();
      setWishlist(guestWishlist);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      // If guest wishlist exists, sync to backend then clear
      const guestWishlist = loadGuestWishlist();
      if (guestWishlist.length > 0) {
        for (const product of guestWishlist) {
          await fetch(getApiUrl('/api/wishlist'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({ product: product._id || product.id })
          });
        }
        localStorage.removeItem('guest_wishlist');
      }
      const res = await fetch(getApiUrl('/api/wishlist'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json();
        setError(err.message || 'Failed to fetch wishlist');
        return;
      }
      const data = await res.json();
      setWishlist(Array.isArray(data) ? data : []);
    } catch (error) {
      setError('Network error while fetching wishlist');
    } finally {
      setIsLoading(false);
    }
  };

  // On mount or token change, load wishlist from backend or localStorage
  useEffect(() => {
    fetchWishlist();
    // eslint-disable-next-line
  }, [token]);

  const isWishlisted = (id) => wishlist.some(item => item._id === id || item.id === id);

  const addToWishlist = async (product) => {
    console.log('addToWishlist called with:', product);
    console.log('Token exists:', !!token);
    
    if (!token) {
      console.log('No token - adding to guest wishlist');
      let guestWishlist = loadGuestWishlist();
      if (!guestWishlist.some(item => (item._id || item.id) === (product._id || product.id))) {
        guestWishlist.push(product);
        saveGuestWishlist(guestWishlist);
        setWishlist(guestWishlist);
        console.log('Added to guest wishlist:', guestWishlist);
      } else {
        console.log('Product already in guest wishlist');
      }
      return;
    }
    setError(null);
    try {
      const res = await fetch(getApiUrl('/api/wishlist'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ product: product._id || product.id })
      });
      if (!res.ok) {
        const err = await res.json();
        setError(err.message || 'Failed to add item to wishlist');
        return;
      }
      await fetchWishlist();
    } catch (error) {
      setError('Network error while adding to wishlist');
    }
  };

  const removeFromWishlist = async (productId) => {
    console.log('removeFromWishlist called with:', productId);
    if (!token) {
      let guestWishlist = loadGuestWishlist();
      guestWishlist = guestWishlist.filter(item => (item._id || item.id) !== productId);
      saveGuestWishlist(guestWishlist);
      setWishlist(guestWishlist);
      console.log('Guest wishlist after removal:', guestWishlist);
      return;
    }
    setError(null);
    try {
      const res = await fetch(getApiUrl(`/api/wishlist/${productId}`), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      console.log('Backend response status:', res.status);
      if (!res.ok) {
        const err = await res.json();
        setError(err.message || 'Failed to remove item from wishlist');
        console.log('Error removing from wishlist:', err);
        return;
      }
      await fetchWishlist();
      console.log('Successfully removed from wishlist and refreshed');
    } catch (error) {
      setError('Network error while removing from wishlist');
      console.log('Network error while removing from wishlist:', error);
    }
  };

  const toggleWishlist = async (product) => {
    console.log('toggleWishlist called with:', product);
    console.log('Current wishlist:', wishlist);
    console.log('Token exists:', !!token);
    
    // Handle both product objects and product IDs
    const productId = typeof product === 'string' ? product : (product._id || product.id);
    const productObj = typeof product === 'string' ? { _id: product } : product;
    
    console.log('Product ID:', productId);
    console.log('Product Object:', productObj);
    console.log('Is wishlisted:', isWishlisted(productId));
    
    if (isWishlisted(productId)) {
      console.log('Removing from wishlist');
      await removeFromWishlist(productId);
    } else {
      console.log('Adding to wishlist');
      await addToWishlist(productObj);
    }
  };

  return (
    <WishlistContext.Provider value={{
      wishlist,
      isWishlisted,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
      isLoading,
      error,
      fetchWishlist
    }}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};
